import { useState, useMemo, useEffect } from 'react';
import { useCrmData } from '../../../contexts/CrmProviders';
import { useAuth } from '../../../contexts/AuthContext';
import { useAppContext } from '../../../contexts/AppContext';
import { DateRange } from '../../../components/shared/filters/DateRangePicker';
import { useAnalysisModal } from './useAnalysisModal';
import { useLeadsMetrics } from './useLeadsMetrics';
import { useSalesMetrics } from './useSalesMetrics';
import { useConversationsByChannel } from './useConversationsByChannel';
import { useDashboardStats } from './useDashboardStats';
import { usePermissions } from '../../../hooks/usePermissions';
import { useDashboardLeads } from './useDashboardLeads';

const DASHBOARD_DATE_RANGE_KEY = 'dashboard_date_range';

/**
 * Utilitário para ler o dateRange do localStorage
 * Retorna o range salvo ou gera um padrão de 7 dias
 */
const getInitialDateRange = (): DateRange => {
    try {
        const saved = localStorage.getItem(DASHBOARD_DATE_RANGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            return {
                start: new Date(parsed.start),
                end: new Date(parsed.end)
            };
        }
    } catch {
        // Em caso de erro, usa o padrão
    }
    
    // Padrão: Este mês
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
};

export const useDashboard = () => {
    const { crmUsers, contacts, isLoading: isCrmLoading } = useCrmData();
    const { user } = useAuth();
    const { currentPage } = useAppContext();
    const { canViewAllLeads } = usePermissions();

    const isDashboardActive = currentPage === 'dashboard';
    const { leads: orgLeads, isLoading: isLoadingOrgLeads } = useDashboardLeads(isDashboardActive);
    const leads = orgLeads;

    // Owner/admin veem tudo; demais papéis só negociações próprias ou sem responsável
    const visibleLeads = useMemo(() => {
        if (canViewAllLeads) {
            return leads;
        }
        return leads.filter(lead =>
            lead.agent === user?.id ||
            !lead.agent ||
            lead.agent === ''
        );
    }, [leads, canViewAllLeads, user?.id]);

    // Estado de filtro de data com persistência no localStorage
    const [dateRange, setDateRange] = useState<DateRange>(getInitialDateRange);

    // Persiste o dateRange no localStorage sempre que mudar
    useEffect(() => {
        try {
            if (!dateRange.start || !dateRange.end) {
                localStorage.removeItem(DASHBOARD_DATE_RANGE_KEY);
                return;
            }
            localStorage.setItem(DASHBOARD_DATE_RANGE_KEY, JSON.stringify({
                start: dateRange.start.toISOString(),
                end: dateRange.end.toISOString()
            }));
        } catch {
            // Ignora erros de escrita no localStorage
        }
    }, [dateRange]);

    // Hook de métricas de leads
    const {
        filteredLeads,
        leadsByDay,
        leadsByStatus
    } = useLeadsMetrics(visibleLeads, dateRange);

    // Análise IA: leads filtrados + usuários (atendimentos são buscados no clique, no período)
    const {
        isAnalysisModalOpen,
        analysisResult: analysisData,
        analysisError,
        isLoadingAnalysis: isAnalyzing,
        handleAnalyze: openAnalysisModal,
        closeAnalysisModal
    } = useAnalysisModal({ leads: filteredLeads, users: crmUsers, dateRange });

    // Hook de métricas de vendas
    const {
        salesByMonth,
        salesByChannel,
        salesRanking,
        ltvRanking
    } = useSalesMetrics(visibleLeads, filteredLeads, crmUsers, contacts);

    const { conversationsByChannel, isLoadingChannels } = useConversationsByChannel(
        dateRange,
        isDashboardActive,
    );

    const isLoading = isDashboardActive ? isLoadingOrgLeads : isCrmLoading;

    // Hook de estatísticas gerais
    const { stats, leadsByLostReason } = useDashboardStats(visibleLeads, filteredLeads, contacts);

    return {
        // Dados principais
        leads: visibleLeads,
        filteredLeads,
        
        // Estado UI
        isAnalysisModalOpen,
        analysisResult: analysisData,
        analysisError,
        isLoadingAnalysis: isAnalyzing,
        handleAnalyze: openAnalysisModal,
        closeAnalysisModal,
        dateRange,
        setDateRange,

        // Métricas de leads
        leadsByDay,
        leadsByStatus,

        // Métricas de vendas
        salesByMonth,
        salesByChannel,
        salesRanking,
        ltvRanking,
        conversationsByChannel,
        isLoadingChannels,

        // Estatísticas
        stats,
        leadsByLostReason,
        isLoading
    };
};
